import {
  cloneElement,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useId,
} from 'react'

type Props = {
  label: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  children: ReactElement
  // For composite layouts (multiple controls under one label). When set, the
  // caller is responsible for putting the same id on the inner focusable control.
  htmlFor?: string
  className?: string
}

const Field = ({
  label,
  hint,
  error,
  required,
  children,
  htmlFor,
  className = '',
}: Props) => {
  const generated = useId()
  if (!isValidElement(children)) {
    throw new Error('Field expects a single React element child')
  }
  const childProps = children.props as { id?: string }
  const controlId = htmlFor ?? childProps.id ?? generated
  // Only clone in the single-control case where we're driving the id ourselves.
  const shouldClone = htmlFor === undefined && childProps.id === undefined
  const cloned = shouldClone
    ? cloneElement(children, { id: controlId } as Record<string, unknown>)
    : children

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      <label
        htmlFor={controlId}
        className="text-xs font-bold uppercase tracking-caps text-ink-500"
      >
        {label}
        {required && (
          <span aria-hidden="true" className="text-flag ml-0.5">
            *
          </span>
        )}
      </label>
      {cloned}
      {error ? (
        <span className="text-xs text-flag">{error}</span>
      ) : hint ? (
        <span className="text-xs text-ink-500">{hint}</span>
      ) : null}
    </div>
  )
}

export default Field
